import React, { useState } from 'react';
import { supabase } from '../supabaseClientConfig';
import { CONDITIONS_VERSION, PAGES_LEGALES, documentsAAccepter } from '../conditions';

export const BENEFICIAIRE_MSG = "Cette adresse email est déjà enregistrée comme bénéficiaire d'un organisme de formation : vous n'avez pas besoin de créer d'espace. Connectez-vous avec le lien reçu par email, ou cliquez sur « Oublié ? » (à côté du mot de passe) sur la page de connexion.";

const SignupPage = () => {
  const [orgName, setOrgName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [estOrganisme, setEstOrganisme] = useState(false);
  const [accepteConditions, setAccepteConditions] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (!estOrganisme) {
      setError("Cochez la case confirmant que vous créez l'espace d'un organisme de formation. Vous êtes bénéficiaire ? Utilisez « Première connexion » sur la page de connexion.");
      return;
    }
    if (!accepteConditions) {
      setError("Pour créer votre espace, cochez la case d'acceptation des conditions générales de vente, des conditions d'utilisation et de l'accord de traitement des données.");
      return;
    }
    setIsLoading(true);
    try {
      // AJOUT (2026-10-01) : cette page sert UNIQUEMENT à créer un nouvel organisme de formation. Des
      // bénéficiaires invités par leur organisme arrivaient ici par erreur ("Créer votre espace" sur la
      // page de connexion) et se retrouvaient administrateurs d'un organisme vide au lieu d'accéder à
      // leur espace client. On refuse donc toute adresse déjà enregistrée comme bénéficiaire. Le vrai
      // verrou est côté base (trigger guard_utilisateurs_roles, voir beneficiaire_admin_guard_migration.sql) ;
      // ce contrôle-ci sert à afficher un message clair AVANT de créer quoi que ce soit.
      const { data: isBeneficiaire, error: checkError } = await supabase.rpc('email_est_beneficiaire', { p_email: email.trim() });
      if (checkError) console.error('[Signup] vérification bénéficiaire impossible :', checkError);
      if (isBeneficiaire === true) {
        setError(BENEFICIAIRE_MSG);
        setIsLoading(false);
        return;
      }
      const { error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: window.location.origin + '/setup-organisation',
          data: {
            org_name: orgName.trim(),
            admin_name: adminName.trim(),
            // CONFORMITÉ (2026-10-23) : acceptation donnée à l'inscription, enregistrée à la 1re connexion.
            conditions_acceptees: { version: CONDITIONS_VERSION, documents: documentsAAccepter('admin'), le: new Date().toISOString() }
          }
        }
      });
      if (authError) throw authError;
      setDone(true);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
    }
    setIsLoading(false);
  };

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3">Vérifiez votre email</h1>
          <p className="text-gray-500 mb-6">
            Un lien de confirmation a été envoyé à <strong>{email}</strong>.
            Cliquez dessus pour activer votre compte.
          </p>
          <button onClick={() => window.location.replace('/')} className="text-sm font-bold text-violet-600 hover:text-violet-700">
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md border border-gray-100">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
        <h1 className="text-2xl font-extrabold text-gray-900 mb-1 text-center">Créer l'espace de votre organisme</h1>
        <p className="text-gray-500 mb-4 text-center text-sm">Réservé aux organismes de formation qui souhaitent utiliser SkorUp.</p>
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 border-2 border-amber-300 text-sm text-amber-900">
          <p className="font-black">Vous êtes accompagné(e) par un organisme, ou formateur ?</p>
          <p className="mt-1 text-xs">Ne remplissez pas ce formulaire : votre organisme vous a déjà inscrit(e). Il vous suffit de créer votre mot de passe.</p>
          <a href="/?premiere-connexion=1" className="mt-3 block text-center bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold py-2.5 rounded-xl">
            Accéder à mon espace (première connexion)
          </a>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Nom de l'organisme</label>
            <input type="text" required value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="ex : Mon Organisme Formation" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Votre nom complet</label>
            <input type="text" required value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="ex : Marie Dupont" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Adresse email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Mot de passe</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="8 caractères minimum" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Confirmer le mot de passe</label>
            <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Répéter le mot de passe" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 cursor-pointer">
            <input type="checkbox" checked={estOrganisme} onChange={e => setEstOrganisme(e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600" />
            <span className="text-xs text-gray-700">Je crée l'espace <strong>d'un organisme de formation</strong> (je ne suis pas bénéficiaire d'un accompagnement).</span>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 cursor-pointer">
            <input type="checkbox" checked={accepteConditions} onChange={e => setAccepteConditions(e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600" />
            <span className="text-xs text-gray-700">
              J'ai lu et j'accepte les{' '}
              <a href={PAGES_LEGALES.cgv.url} target="_blank" rel="noopener noreferrer" className="font-bold text-violet-700 underline">conditions générales de vente</a>, les{' '}
              <a href={PAGES_LEGALES.cgu.url} target="_blank" rel="noopener noreferrer" className="font-bold text-violet-700 underline">conditions d'utilisation</a> et l'
              <a href={PAGES_LEGALES['accord-traitement-donnees'].url} target="_blank" rel="noopener noreferrer" className="font-bold text-violet-700 underline">accord de traitement des données</a>.
              {' '}Voir aussi la <a href={PAGES_LEGALES.confidentialite.url} target="_blank" rel="noopener noreferrer" className="underline">politique de confidentialité</a>.
            </span>
          </label>
          {error && <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{error}</p>}
          <button type="submit" disabled={isLoading || !estOrganisme || !accepteConditions} className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all disabled:opacity-50">
            {isLoading ? 'Création en cours...' : "Créer l'espace de mon organisme"}
          </button>
        </form>
        <p className="text-center text-sm text-gray-500 mt-6">
          Déjà inscrit ?{' '}
          <button onClick={() => window.location.replace('/')} className="font-bold text-violet-600 hover:text-violet-700">Se connecter</button>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
